import CapacitorStorage from 'Utils/CapacitorStorage';
import { reportError } from 'Utils/reportError';
import { DEFAULT_VOLUME_SETTINGS, VolumeSettings, normalizeVolumeSettings } from './volumeSettings';

/**
 * Volume settings live in the per-user namespace of the app's persisted storage
 * (`<user id>-museflow_volume_settings`), so each account on a device keeps its
 * own levels. They are a device preference, not account data: sign-out carries
 * them through its storage clear (see `isVolumeSettingsStorageKey`).
 */
export const VOLUME_SETTINGS_STORAGE_KEY = 'museflow_volume_settings';

/** Whether a full storage key (any user prefix included) holds volume settings. */
export const isVolumeSettingsStorageKey = (storageKey: string): boolean =>
  storageKey === VOLUME_SETTINGS_STORAGE_KEY ||
  storageKey.endsWith(`-${VOLUME_SETTINGS_STORAGE_KEY}`);

/** A user's saved settings, or the defaults when none are saved or readable. */
export const loadVolumeSettings = async (userId: string | undefined): Promise<VolumeSettings> => {
  try {
    const stored = await CapacitorStorage.getItemFor(userId, VOLUME_SETTINGS_STORAGE_KEY);
    return typeof stored === 'string' && stored !== ''
      ? normalizeVolumeSettings(JSON.parse(stored))
      : DEFAULT_VOLUME_SETTINGS;
  } catch (e) {
    reportError(e, 'volume.load');
    return DEFAULT_VOLUME_SETTINGS;
  }
};

export const saveVolumeSettings = async (
  userId: string | undefined,
  settings: VolumeSettings,
): Promise<void> => {
  await CapacitorStorage.setItemFor(userId, VOLUME_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
};
